import { Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspace } from "~/api/queries";
import { VoiceProvider } from "~/features/talk/voice";
import { WorkspaceThinkerDock } from "~/features/thinkers/workspace/WorkspaceThinkerDock";
import { useThinkerDock } from "~/features/work/piles/pilesApi";
import { Scrollable } from "~/foundation/components/Scrollable";
import { mediaQueries } from "~/foundation/styles/breakpoints";
import { depths } from "~/foundation/styles/depths";
import { cn } from "~/foundation/utils/cn";
import { AskBar } from "~/shell/askbar/AskBar";
import { CommandBar } from "~/shell/CommandBar";
import { ConsoleBar } from "~/shell/ConsoleBar/ConsoleBar";
import { DialogHost } from "~/shell/DialogHost";
import { Rail } from "~/shell/Sidebar/Rail";
import { Sidebar } from "~/shell/Sidebar/Sidebar";
import { SidebarResizeHandle } from "~/shell/Sidebar/SidebarResizeHandle";
import { useGlobalShortcuts } from "~/shell/shortcuts";
import { clampSidebarWidth, useShellStore } from "~/shell/store";

function useDrawerDismissal(): void {
  const setMobileSidebarOpen = useShellStore((state) => state.setMobileSidebarOpen);

  // `?ask=<text>` opens the Ask AI bar with that text (2026-10-21): the VS Code "Docit: Ask AI…" command opens the
  // Docit panel at the current page with it, so the extension has the same bar as the desk.
  const askParam = useRouterState({
    select: (state) => new URLSearchParams(state.location.searchStr).get("ask"),
  });
  const openAskBar = useThinkerDock((state) => state.show);
  useEffect(() => {
    if (askParam) openAskBar(askParam);
  }, [askParam, openAskBar]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    const mql = window.matchMedia(mediaQueries.mobile);
    const onChange = () => {
      if (!mql.matches) {
        setMobileSidebarOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileSidebarOpen(false);
      }
    };
    mql.addEventListener("change", onChange);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      mql.removeEventListener("change", onChange);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [setMobileSidebarOpen]);
}

/**
 * The whole shell in one grid:
 *
 *   rows:    48px console bar | everything else
 *   columns: sidebar (240px | 48px | 0) | the scene
 *
 * Only the scene scrolls.
 */
export function AuthenticatedLayout() {
  const { t } = useTranslation();
  const { data: workspace } = useCurrentWorkspace();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const thinkerOpen = useThinkerDock((state) => state.open);
  const closeRightDrawer = useShellStore((state) => state.closeRightDrawer);
  useEffect(() => {
    if (thinkerOpen) closeRightDrawer();
  }, [thinkerOpen, closeRightDrawer]);

  const sidebarCollapsed = useShellStore((state) => state.sidebarCollapsed);
  const sidebarWidth = useShellStore((state) => state.sidebarWidth);
  const sidebarResizing = useShellStore((state) => state.sidebarResizing);
  const mobileSidebarOpen = useShellStore((state) => state.mobileSidebarOpen);
  const setMobileSidebarOpen = useShellStore((state) => state.setMobileSidebarOpen);
  const setSidebarCollapsed = useShellStore((state) => state.setSidebarCollapsed);

  useGlobalShortcuts();
  useDrawerDismissal();

  const abilities = workspace?.abilities ?? {};

  return (
    // The voice (who answers, with which model) and the live operation are shell-wide: the
    // header's model chooser shows them on every page.
    <VoiceProvider>
      <div
        className="grid h-dvh w-full overflow-hidden bg-bg [--console-bar-height:88px] lg:[--console-bar-height:48px]"
        style={{
          gridTemplateRows: "var(--console-bar-height) 1fr",
          gridTemplateColumns: "auto 1fr",
        }}
      >
        <ConsoleBar />

        {/* Desktop sidebar column: 240px, the 48px rail, or nothing at all on mobile. */}
        <aside
          className={cn(
            "relative hidden overflow-hidden border-e border-divider bg-sidebar-bg",
            thinkerOpen ? "lg:block" : "md:block",
            // The width transition serves collapse/expand; a live drag must track the pointer.
            !sidebarResizing &&
              "transition-[width] duration-200 ease-in-out motion-reduce:transition-none",
          )}
          style={{
            zIndex: depths.sidebar,
            width: sidebarCollapsed
              ? "var(--sidebar-rail-width)"
              : `${thinkerOpen ? Math.min(260, clampSidebarWidth(sidebarWidth)) : clampSidebarWidth(sidebarWidth)}px`,
          }}
        >
          {sidebarCollapsed ? (
            <Rail
              abilities={abilities}
              pathname={pathname}
              onExpand={() => setSidebarCollapsed(false)}
            />
          ) : (
            <>
              <Sidebar abilities={abilities} pathname={pathname} />
              <SidebarResizeHandle />
            </>
          )}
        </aside>

        <div className="col-start-1 col-end-3 flex min-h-0 min-w-0 overflow-hidden md:col-start-2">
          <main
            className={cn(
              "min-h-0 min-w-0 flex-1 overflow-hidden",
              thinkerOpen && "hidden lg:block",
            )}
          >
            <Scrollable className="h-full">
              <Outlet />
            </Scrollable>
          </main>
          <WorkspaceThinkerDock />
        </div>

        {/* Mobile drawer — overlays from the inline-start edge, which flips under RTL. */}
        {mobileSidebarOpen ? (
          <button
            type="button"
            aria-label={t("Close sidebar")}
            className="fixed inset-0 bg-backdrop md:hidden"
            style={{ zIndex: depths.sidebar }}
            onClick={() => setMobileSidebarOpen(false)}
          />
        ) : null}
        <div
          aria-hidden={!mobileSidebarOpen}
          className={cn(
            "fixed inset-y-0 start-0 w-[var(--sidebar-drawer-width)] border-e border-divider bg-sidebar-bg md:hidden",
            "transition-transform duration-200 ease-in-out motion-reduce:transition-none",
            mobileSidebarOpen
              ? "translate-x-0"
              : "pointer-events-none -translate-x-full rtl:translate-x-full",
          )}
          style={{ zIndex: depths.sidebar + 1 }}
        >
          <Sidebar
            abilities={abilities}
            pathname={pathname}
            onNavigate={() => setMobileSidebarOpen(false)}
          />
        </div>

        <CommandBar />
        {/* W7 — Ask mode, a sibling of the command bar and never on screen at the same time. */}
        <AskBar />
        <DialogHost />
      </div>
    </VoiceProvider>
  );
}
